<template>
  <div :class="['tm-spielplan', { 'tm-spielplan--vollbild': istVollbild }]">
    <div class="tm-kopf-bereich">
      <div class="tm-toolbar">
        <div class="tm-search">
          <svg class="tm-search__icon" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <circle cx="8.5" cy="8.5" r="6" stroke="currentColor" stroke-width="1.6"/>
            <path d="M13.3 13.3 17.5 17.5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
          </svg>
          <input v-model="teamFilter" placeholder="Team suchen …" class="tm-search__input" />
          <button
            v-if="teamFilter"
            type="button"
            class="tm-search__clear"
            aria-label="Suche zurücksetzen"
            @click="teamFilter = ''"
          >✕</button>
        </div>

        <LigaDropdown
          ref="ligaDropdownRef"
          :ligen="ligen"
          :model-value="activeLiga"
          @toggle="ligaDropdownUmschalten"
        />

        <div class="tm-toggle" role="group" aria-label="Ansicht wählen">
          <button type="button" :class="['tm-toggle__btn', { 'tm-toggle__btn--active': view === 'tag' }]" @click="view = 'tag'">Tag</button>
          <button type="button" :class="['tm-toggle__btn', { 'tm-toggle__btn--active': view === 'gruppe' }]" @click="view = 'gruppe'">Liga</button>
        </div>

        <button type="button" class="tm-menu-btn" aria-label="Menü öffnen" @click="zeigeMenue = true">
          <svg viewBox="0 0 20 20" fill="none" width="18" height="18" aria-hidden="true">
            <circle cx="10" cy="4" r="1.4" fill="currentColor"/>
            <circle cx="10" cy="10" r="1.4" fill="currentColor"/>
            <circle cx="10" cy="16" r="1.4" fill="currentColor"/>
          </svg>
        </button>
      </div>

      <div v-if="verfuegbareTage.length > 0 && !sucheAktiv" class="tm-day-nav" ref="tagNavRef">

        <div class="tm-day-nav-1">
          <button
            type="button"
            class="tm-day-nav__btn"
            :disabled="ausgewaehlterTagIndex === 0"
            @click="vorherigerTag"
            aria-label="Vorheriger Tag"
          >‹</button>

          <button type="button" class="tm-day-nav__label tm-day-nav__label--klickbar" @click="kalenderUmschalten">
            <span class="tm-day-nav__weekday">{{ tagLabel.wochentagShort }}</span>
            <span class="tm-day-nav__date">{{ tagLabel.datum }}</span>
          </button>

          <button
            type="button"
            class="tm-day-nav__btn"
            :disabled="ausgewaehlterTagIndex === verfuegbareTage.length - 1"
            @click="naechsterTag"
            aria-label="Nächster Tag"
          >›</button>
        </div>

        <div class="tm-day-nav-2">
          <button type="button" class="tm-day-nav__today" @click="springeZuHeute" aria-label="Zu heute springen">📍</button>
          <button type="button" class="tm-day-nav__today" @click="zeigeRaster = true" aria-label="Zeitraster">▦</button>
          <button
            v-if="ligaFavoriten.size > 0"
            type="button"
            class="tm-liga-mini-toggle"
            :class="{ 'tm-liga-mini-toggle--aktiv': activeLiga === 'favoriten' }"
            :aria-label="activeLiga === 'favoriten' ? 'Alle Ligen zeigen' : 'Nur Favoriten-Ligen zeigen'"
            @click="activeLiga = activeLiga === 'favoriten' ? 'alle' : 'favoriten'"
          >⭐</button>
        </div>
      </div>
    </div>

    <div class="tm-inhalt-bereich" v-scroll-melden>
      <div v-if="loading" class="tm-state">
        <span class="tm-state__spinner" aria-hidden="true"></span>
        Spielplan wird geladen …
      </div>

      <div v-else-if="error" class="tm-state tm-state--error tm-state--column">
        Spielplan konnte nicht geladen werden ({{ error }}). Neuer Versuch läuft automatisch im Hintergrund.
        <button type="button" class="tm-retry-btn" @click="manuellerRetry">Jetzt erneut versuchen</button>
      </div>

      <div v-else class="tm-inhalt-liste">
        <div v-if="(sucheAktiv ? teamTrefferAlleTage : filteredMatches).length === 0" class="tm-state">
          Keine Spiele zu diesem Filter. Team oder Liga anders wählen.
        </div>

        <!-- Team-Suche aktiv + Tag-Ansicht: flache, datumssortierte Liste ÜBER ALLE TAGE hinweg -
             die Tages-Navigation ergibt hier keinen Sinn mehr, der Nutzer will ja alle Treffer
             sehen, nicht nur die vom aktuell ausgewählten Tag. -->
        <div v-else-if="sucheAktiv && view === 'tag'" ref="sucheListeRef" class="tm-card-list">
          <div v-for="match in teamTrefferAlleTage" :key="match.id" :ref="el => registriereSucheZeile(match.id, el)">
            <MatchCard
              :match="match"
              :jetzt="jetzt"
              :countdown-ab-minuten="props.countdownAbMinuten"
              :zeige-datum="true"
              @open-detail="oeffneDetail"
              :tabellen="tabellen"
              @open-standings="match => zeigeTabelleFuer = match"
            />
          </div>
        </div>

        <!-- Team-Suche aktiv + Liga-Ansicht: dieselben Suchtreffer, aber nach Liga gruppiert -
             der Liga/Tag-Umschalter soll auch während einer aktiven Suche funktionieren. -->
        <template v-else-if="sucheAktiv">
          <div v-for="gruppe in teamTrefferGruppiertNachLiga" :key="gruppe.ligaId" class="tm-group">
            <h3 class="tm-group__heading">{{ gruppe.label }}</h3>
            <div class="tm-card-list">
              <MatchCard
                v-for="match in gruppe.matches"
                :key="match.id"
                :match="match"
                :jetzt="jetzt"
                :countdown-ab-minuten="props.countdownAbMinuten"
                :zeige-datum="true"
                @open-detail="oeffneDetail"
                :tabellen="tabellen"
                @open-standings="match => zeigeTabelleFuer = match"
              />
            </div>
          </div>
        </template>

        <!-- Ansicht: Tag (chronologisch, keine Gruppierung) -->
        <div v-else-if="view === 'tag'" class="tm-card-list">
          <MatchCard
            v-for="match in filteredMatchesSortiert"
            :key="match.id"
            :match="match"
            :jetzt="jetzt"
            :countdown-ab-minuten="props.countdownAbMinuten"
            @open-detail="oeffneDetail"
            :tabellen="tabellen"
            @open-standings="match => zeigeTabelleFuer = match"
          />
        </div>

        <!-- Ansicht: Liga (gruppiert) -->
        <template v-else>
          <div v-for="gruppe in gruppiertNachLiga" :key="gruppe.ligaId" class="tm-group">
            <h3 class="tm-group__heading">{{ gruppe.label }}</h3>
            <div class="tm-card-list">
              <MatchCard
                v-for="match in gruppe.matches"
                :key="match.id"
                :match="match"
                :jetzt="jetzt"
                :countdown-ab-minuten="props.countdownAbMinuten"
                @open-detail="oeffneDetail"
                :tabellen="tabellen"
                @open-standings="match => zeigeTabelleFuer = match"
              />
            </div>
          </div>
        </template>
      </div>
    </div>

    <p v-if="wsStatus !== 'verbunden'" class="tm-footer">Live-Updates: {{ wsStatus }}</p>

    <Teleport :to="portalZiel">
      <SpielplanRasterModal
        v-if="zeigeRaster"
        :matches="filteredMatchesSortiert"
        :label="formatTag(ausgewaehlterTag)"
        :ziel="portalZiel"
        @close="zeigeRaster = false"
        @select="m => { oeffneDetail(m) }"
        @prev="tagVerschieben(-1)" 
        @next="tagVerschieben(1)"
      />
      <Transition name="tm-kalender-auf">
        <LigaDropdownPanel
          v-if="zeigeLigaDropdown"
          class="tm-liga-dropdown__panel--teleportiert"
          :style="ligaDropdownPositionStyle"
          :ligen="ligen"
          :model-value="activeLiga"
          @select="ligaAuswaehlen"
        />
      </Transition>
      <div v-if="zeigeLigaDropdown" class="tm-kalender-backdrop" @click="zeigeLigaDropdown = false"></div>

      <Transition name="tm-kalender-auf">
        <KalenderPopover
          v-if="zeigeKalender"
          class="tm-kalender-popover tm-kalender-popover--teleportiert"
          :style="kalenderPositionStyle"
          :verfuegbare-tage="verfuegbareTage"
          :spieltage-gruppen="spieltageGruppen"
          :max-badges="props.kalenderMaxLigen"
          :ausgewaehlter-tag="ausgewaehlterTag"
          @select-day="springeZuTag"
        />
      </Transition>
      <div v-if="zeigeKalender" ref="kalenderElement" class="tm-kalender-backdrop" @click="zeigeKalender = false"></div>

      <Transition name="tm-sheet-fade">
        <div v-if="offenesDetail || zeigeRangliste || zeigeMenue || zeigeTabelleFuer" class="tm-sheet-backdrop" @click="schliesseAlleOverlays"></div>
      </Transition>
      <Transition name="tm-sheet-slide">
        <DetailOverlay
          v-if="offenesDetail"
          :detail="offenesDetail"
          :api-base="props.apiBase"
          :tabellen="tabellen"
          @close="schliesseDetail"
          @change-tab="wechsleDetailTab"
        />
      </Transition>
      <Transition name="tm-sheet-slide">
        <RanglisteOverlay
          v-if="zeigeRangliste"
          :rangliste="rangliste"
          @close="zeigeRangliste = false"
        />
      </Transition>
      <Transition name="tm-sheet-slide">
        <MenueOverlay
          v-if="zeigeMenue"
          :nur-favoriten="nurFavoriten"
          :ist-vollbild="istVollbild"
          @close="zeigeMenue = false"
          @toggle-favoriten="nurFavoriten = !nurFavoriten; zeigeMenue = false"
          @toggle-vollbild="istVollbild = !istVollbild; zeigeMenue = false"
          @oeffne-rangliste="zeigeMenue = false; zeigeRangliste = true"
        />
      </Transition>
      <Transition name="tm-sheet-slide">
        <StandingsOverlay
          v-if="zeigeTabelleFuer"
          :tabellen="tabellen"
          :match="zeigeTabelleFuer"
          :api-base="props.apiBase"
          @close="zeigeTabelleFuer = null"
        />
      </Transition>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue'
import { formatTagAnzeige, formatTagZweiteilig, formatTag, tagSchluessel, ermittleWsUrl } from './lib/format.js'
import { useSpielplanData } from './composables/useSpielplanData.js'
import { useLigaFavoriten } from './composables/useLigaFavoriten.js'
import { useLiveScores } from './composables/useLiveScores.js'
import { useClock } from './composables/useClock.js'
import { useFavoriten } from './composables/useFavoriten.js'
import MatchCard from './components/MatchCard.vue'
import DetailOverlay from './components/DetailOverlay.vue'
import KalenderPopover from './components/KalenderPopover.vue'
import LigaDropdown from './components/LigaDropdown.vue'
import LigaDropdownPanel from './components/LigaDropdownPanel.vue'
import RanglisteOverlay from './components/RanglisteOverlay.vue'
import MenueOverlay from './components/MenueOverlay.vue'
import StandingsOverlay from './components/StandingsOverlay.vue'
import { getPortalTarget } from './lib/portal.js'
import { vScrollMelden } from './lib/scrollLock.js'
import { useKalenderPopover } from './composables/useKalenderPopover.js'
import SpielplanRasterModal from './components/SpielplanRasterModal.vue'
const zeigeRaster = ref(false)

/**
 * Props:
 *  - scope: 'global' | 'veranstalter' -> steuert, welche Spiele geladen werden
 *  - veranstalterId: nötig wenn scope === 'veranstalter'
 *  - apiBase: REST-Basis-URL (Spielplan inkl. Ergebnis + Quoten)
 *  - wsUrl: WebSocket-URL für Live-Score-Updates (bestehender Socket aus der Tipp-App)
 *  - von/bis: optionaler Datumsbereich 'YYYY-MM-DD', sonst dynamischer Default
 *  - priorityLeagues: Liga-IDs, die das Backend priorisiert ausliefert/sortiert
 */
const props = defineProps({
  scope: { type: String, default: 'global' },
  veranstalterId: { type: String, default: null },
  companyId: { type: Number, required: true },
  // Optional: expliziter Cache-Schlüssel, der die automatische Herleitung aus den anderen Props
  // überstimmt - z.B. cache-key="startseite-hauptseite" auf BEIDEN Widgets gesetzt garantiert
  // denselben geteilten Fetch, unabhängig von sonstigen Attribut-Abweichungen.
  cacheKey: { type: String, default: null },
  apiBase: { type: String, default: '/rest/courseservice/oddsservice/free' },
  // Basis-URL für relative Bild-Pfade in Story-Daten (imageUrl/avatarUrl) und Länderflaggen -
  // Default die Produktions-Domain, lokal beim Testen z.B. auf "" setzen (siehe
  // useSpielplanData.js: bildBasisUrl).
  bildBasisUrl: { type: String, default: 'https://app.team-match.io' },
  wsUrl: { type: String, default: '/ws-result-service' },
  von: { type: String, default: null },
  bis: { type: String, default: null },
  priorityLeagues: { type: [Array, String], default: () => [] },
  // Wie viele Liga-Badges pro Kalendertag angezeigt werden, bevor '+N' zusammenfasst
  kalenderMaxLigen: { type: Number, default: 2 },
  // Ab wie vielen Minuten vor Anpfiff der Countdown clientseitig gezeigt wird, unabhängig vom Server-Status
  countdownAbMinuten: { type: Number, default: 120 }
})

const view = ref('tag')
// Portal-Ziel für die Overlays - ein Element direkt an <body>, mit eigenem Shadow-Root und
// denselben Styles nochmal injiziert, damit die Overlays aus jedem Stacking-Context der
// Host-Seite ausbrechen können (siehe lib/portal.js für die ausführliche Begründung).
const portalZiel = getPortalTarget()

const istVollbild = ref(false)

const { zeigeKalender, tagNavRef, kalenderPositionStyle, kalenderUmschalten, kalenderSchliessen } = useKalenderPopover()

const zeigeLigaDropdown = ref(false)
const ligaDropdownRef = ref(null) // Referenz auf den Trigger-Button, für die Panel-Position
const ligaDropdownPositionStyle = ref({})

/*
const ausgewaehlterTagSchluessel = computed(() => {
  const d = new Date()
  d.setDate(d.getDate() + tagOffset.value)
  return tagSchluessel(d)
})*/

function tagVerschieben(delta) {
  const neu = verfuegbareTage.value[ausgewaehlterTagIndex.value + delta]
  if (neu) ausgewaehlterTagSchluessel.value = neu
}

const tagLabel = computed(() => formatTagAnzeige(ausgewaehlterTag.value))

function ligaDropdownUmschalten() {
  if (zeigeLigaDropdown.value) {
    zeigeLigaDropdown.value = false
    return
  }
  const rect = ligaDropdownRef.value?.triggerRef?.getBoundingClientRect()
  if (rect) {
    ligaDropdownPositionStyle.value = {
      top: `${rect.bottom + 6}px`,
      left: `${rect.left}px`,
      minWidth: `${rect.width}px`
    }
  }
  zeigeLigaDropdown.value = true
}

function ligaAuswaehlen(ligaId) {
  activeLiga.value = ligaId
  zeigeLigaDropdown.value = false
}

const offenesDetail = ref(null) // { match, tab: 'aufstellung' | 'events' | 'wechsel' | 'statistik' }
const zeigeRangliste = ref(false)
const zeigeMenue = ref(false)
const zeigeTabelleFuer = ref(null) // das Match, für das gerade die Tabelle offen ist

async function oeffneDetail(match, tab) {
  await ladeStoriesFuerSichtbare([match])
  offenesDetail.value = { match, tab }
}

function schliesseDetail() {
  offenesDetail.value = null
}
function schliesseAlleOverlays() {
  offenesDetail.value = null
  zeigeRangliste.value = false
  zeigeMenue.value = false
  zeigeTabelleFuer.value = null
}
function wechsleDetailTab(tab) {
  if (offenesDetail.value) offenesDetail.value = { ...offenesDetail.value, tab }
}

// Sperrt das Scrollen der Host-Seite dahinter, solange irgendein Overlay/Vollbild aktiv ist
// (v.a. auf iOS Safari wichtig, sonst "durchscrollt" die Seite sichtbar hinter dem abgedunkelten Sheet)
const istIrgendeinOverlayOffen = computed(() =>
  istVollbild.value || !!offenesDetail.value || zeigeRangliste.value || zeigeMenue.value || !!zeigeTabelleFuer.value
)
watch(istIrgendeinOverlayOffen, (aktiv) => {
  document.body.style.overflow = aktiv ? 'hidden' : ''
})

const { istFavorit } = useFavoriten()
const { ligaFavoriten } = useLigaFavoriten()

const {
  matches, loading, error, teamFilter, activeLiga, nurFavoriten, tabellen,
  ligen, filteredMatches, gruppiertNachLiga, sucheAktiv, teamTrefferAlleTage, teamTrefferGruppiertNachLiga,
  verfuegbareTage, ausgewaehlterTag, ausgewaehlterTagIndex, ausgewaehlterTagSchluessel, spieltageGruppen,
  vorherigerTag, naechsterTag, springeZuHeute, springeZuTag: springeZuTagRoh,
  loadMatches, manuellerRetry, refreshMatches, aufraeumen: datenAufraeumen, ladeStoriesFuerSichtbare
} = useSpielplanData(props, { istFavorit })

function springeZuTag(schluessel) {
  springeZuTagRoh(schluessel)
  kalenderSchliessen()
}

const { 
  wsStatus, rangliste, verbinden: verbindeWebSocket, 
  aufraeumen: wsAufraeumen, wiederholeGepuffertes, onReaktiviert
} = useLiveScores(ermittleWsUrl(props.wsUrl), matches)
const { jetzt } = useClock() // tickt jede Sekunde, für den Countdown bei Status 'bald'
const stopReaktiviert = onReaktiviert(loadMatches)

// Stories für die gerade sichtbare Auswahl nachladen (aktueller Tag + Team-/Liga-Filter) - lief
// vorher NUR auf der Startseite, hier im eigentlichen Spielplan fehlte der Aufruf komplett, d.h.
// Stories kamen dort nie an (weder Vorschau- noch Spielbericht-Buttons wurden je befüllt).
watch(filteredMatches, (sichtbar) => {
  ladeStoriesFuerSichtbare(sichtbar)
}, { immediate: true })

// Bei aktiver Team-Suche wird ja teamTrefferAlleTage angezeigt statt filteredMatches - Stories
// dafür separat nachladen, sonst blieben Story-Buttons in den Suchergebnissen leer.
watch(teamTrefferAlleTage, (sichtbar) => {
  if (sucheAktiv.value) ladeStoriesFuerSichtbare(sichtbar)
})

// ---------- Auto-Scroll zum nächsten offenen/laufenden Spiel in den Suchergebnissen ----------
// Die Suche zeigt ja auch VERGANGENE Spiele (über alle Tage hinweg, chronologisch sortiert) -
// ohne Auto-Scroll müsste man erst an allen bereits gespielten Treffern vorbeiscrollen, um zum
// eigentlich interessanten (nächsten/laufenden) Spiel zu kommen. Gleiches Muster wie in
// StartseiteWidget.vue.
const sucheListeRef = ref(null)
const sucheZeilenElemente = new Map() // match.id -> DOM-Element

function registriereSucheZeile(matchId, el) {
  if (el) sucheZeilenElemente.set(matchId, el)
  else sucheZeilenElemente.delete(matchId)
}

function scrolleZuNaechstemSucheTreffer() {
  const ziel = teamTrefferAlleTage.value.find(m => m.status !== 'beendet')
  if (!ziel) return
  const el = sucheZeilenElemente.get(ziel.id)
  if (el) el.scrollIntoView({ block: 'start' })
}

// Läuft, sobald sich die Suchergebnisse ändern (neuer Suchbegriff) UND die Suche aktiv ist -
// nextTick, damit die Zeilen-Refs schon im DOM stehen, bevor gescrollt wird.
watch(teamTrefferAlleTage, () => {
  if (!sucheAktiv.value) return
  nextTick(() => scrolleZuNaechstemSucheTreffer())
})

// Favoriten-Spiele zuerst, chronologische Reihenfolge bleibt innerhalb der beiden Gruppen erhalten
const filteredMatchesSortiert = computed(() => {
  return [...filteredMatches.value].sort((a, b) => {
    const af = istFavorit(a.heimId) || istFavorit(a.auswaertsId)
    const bf = istFavorit(b.heimId) || istFavorit(b.auswaertsId)
    if (af === bf) return 0
    return af ? -1 : 1
  })
})
onMounted(async () => {
  await loadMatches()
  verbindeWebSocket()
  wiederholeGepuffertes() // falls die geteilte Verbindung schon vorher (durch ein anderes
  // Widget, z.B. die Startseite) offen war und Nachrichten verpasst wurden, hier nachholen

  // Deep-Link: falls die Seite mit ?tm-match=<id> aufgerufen wurde (z.B. über den Teilen-Button),
  // direkt zum passenden Tag springen und das Detail-Overlay öffnen.
  const geteilteId = new URLSearchParams(window.location.search).get('tm-match')
  if (geteilteId) {
    const gefunden = matches.value.find(m => String(m.id) === geteilteId)
    if (gefunden) {
      springeZuTag(tagSchluessel(gefunden.zeit))
      oeffneDetail(gefunden, 'aufstellung')
    }
  }
})

onUnmounted(() => {
  wsAufraeumen()
  datenAufraeumen()
  stopReaktiviert()
  document.body.style.overflow = '' // falls Vollbild noch aktiv war
})
</script>